import React, { useEffect, useRef, useState } from 'react';
import { TENANT_COMPANY_CODE } from '../../../config/tenant';
import {
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Chip,
    InputAdornment,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Backdrop,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    PersonSearch as PersonSearchIcon,
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    Send as SendIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import spotterTrainingService from '../../../services/spotterTrainingService';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const TRANSLATIONS = {
    english: {
        title: 'Spotter Training',
        information: 'Information',
        spotterName: 'SPOTTER NAME *',
        supervisorName: 'SUPERVISOR NAME *',
        date: 'DATE *',
        definitionTitle: 'Definition',
        definitionBody: 'A spotter is an individual trained to look. A spotter is a person designated to observe and direct equipment operators and drivers to maintain a safe working area.',
        definitionStrong: 'A spotter can have no other job duties while working as a spotter.',
        whenRequiredTitle: 'When is a Spotter Required?',
        whenRequiredIntro: (company) => `OSHA requires the use of spotters for backing vehicles with obstructed rearview. ${company} requires the use of a Spotter when operating mobile equipment. This is especially important when operating with the following conditions:`,
        whenRequiredList: [
            'Entering or moving a vehicle or machinery within a congested area, or through narrow passageways.',
            'Visibility is poor or there is a blind spot',
            'Moving equipment or materials through narrow passageways.',
            'Co-workers, vehicle traffic, or other contractors are in the work area.',
            'Overhead power lines, overhead obstructions and other obstructions present a hazard.'
        ],
        responsibilitiesTitle: 'Spotter Responsibilities & Duties',
        responsibilitiesList: [
            'Communicate with the equipment operator prior to the task, and agree on hand signals or other communication.',
            'Understand the capabilities and limitations of the equipment: turning radius, braking time, etc.',
            'Stand where the equipment operator can see you – if you cannot see the operator then they cannot see you.',
            'Use hand signals as often as possible. Because of noise, a voice signal may be unheard or misunderstood; again, always confirm the meaning of hand signals with the operator prior to spotting.',
            'Always make eye contact with the operator before communicating signals.',
            "If you need to pass through on the operator's blindspot, make sure the operator understands your actions.",
            "Be mindful of your distance from the equipment. When possible, remain 5' outside of the machine's perimeter, away from the tires."
        ],
        noticeLine1: 'Always stay focused and avoid distractions.',
        noticeLine2: 'The use of mobile phones is strictly prohibited while spotting.',
        signaturesTitle: 'Signatures',
        spotterLabel: 'Spotter',
        supervisorLabel: 'Supervisor',
        notSelected: 'Not selected',
        clickToSign: 'Click to Sign',
        clear: 'Clear',
        submit: 'Submit',
        submitting: 'Submitting...',
        draft: 'Draft',
        submitted: 'Submitted'
    },
    spanish: {
        title: 'Capacitación del Vigilante (Spotter)',
        information: 'Información',
        spotterName: 'NOMBRE DEL VIGILANTE *',
        supervisorName: 'NOMBRE DEL SUPERVISOR *',
        date: 'FECHA *',
        definitionTitle: 'Definición',
        definitionBody: 'Un vigilante (spotter) es una persona capacitada para observar. Un vigilante es una persona designada para observar y dirigir a los operadores de equipos y conductores con el fin de mantener un área de trabajo segura.',
        definitionStrong: 'Un vigilante no puede tener ninguna otra responsabilidad laboral mientras esté trabajando como vigilante.',
        whenRequiredTitle: '¿Cuándo se requiere un Vigilante?',
        whenRequiredIntro: (company) => `OSHA requiere el uso de vigilantes para vehículos en retroceso con visibilidad trasera obstruida. ${company} requiere el uso de un Vigilante al operar equipos móviles. Esto es especialmente importante al operar bajo las siguientes condiciones:`,
        whenRequiredList: [
            'Al ingresar o mover un vehículo o maquinaria dentro de un área congestionada, o a través de pasajes estrechos.',
            'La visibilidad es deficiente o existe un punto ciego.',
            'Al mover equipos o materiales a través de pasajes estrechos.',
            'Compañeros de trabajo, tráfico vehicular u otros contratistas se encuentran en el área de trabajo.',
            'Cables eléctricos aéreos, obstrucciones aéreas u otras obstrucciones representan un peligro.'
        ],
        responsibilitiesTitle: 'Responsabilidades y Deberes del Vigilante',
        responsibilitiesList: [
            'Comunicarse con el operador del equipo antes de la tarea y acordar señales con las manos u otro tipo de comunicación.',
            'Entender las capacidades y limitaciones del equipo: radio de giro, tiempo de frenado, etc.',
            'Ubicarse donde el operador del equipo pueda verlo — si usted no puede ver al operador, entonces el operador no puede verlo a usted.',
            'Usar señales con las manos tan frecuentemente como sea posible. Debido al ruido, una señal verbal puede no escucharse o ser malinterpretada; nuevamente, siempre confirme el significado de las señales con las manos con el operador antes de comenzar a vigilar.',
            'Siempre haga contacto visual con el operador antes de comunicar las señales.',
            'Si necesita pasar por el punto ciego del operador, asegúrese de que el operador entienda sus acciones.',
            "Tenga en cuenta su distancia respecto al equipo. Cuando sea posible, manténgase a 5' fuera del perímetro de la máquina, alejado de las llantas."
        ],
        noticeLine1: 'Mantenga siempre la concentración y evite las distracciones.',
        noticeLine2: 'El uso de teléfonos móviles está estrictamente prohibido mientras vigila.',
        signaturesTitle: 'Firmas',
        spotterLabel: 'Vigilante',
        supervisorLabel: 'Supervisor',
        notSelected: 'No seleccionado',
        clickToSign: 'Haga clic para firmar',
        clear: 'Borrar',
        submit: 'Enviar',
        submitting: 'Enviando...',
        draft: 'Borrador',
        submitted: 'Enviado'
    }
};

export default function SpotterTrainingForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        spotterEmployeeId: '',
        spotterName: '',
        spotterSignature: '',
        supervisorEmployeeId: userId,
        supervisorName: userFullName,
        supervisorSignature: '',
        date: dayjs().format('YYYY-MM-DD'),
        language: 'english',
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const t = TRANSLATIONS[form.language === 'spanish' ? 'spanish' : 'english'];

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [spotterModalOpen, setSpotterModalOpen] = useState(false);
    const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
    const [spotterSignatureModalOpen, setSpotterSignatureModalOpen] = useState(false);
    const [supervisorSignatureModalOpen, setSupervisorSignatureModalOpen] = useState(false);

    const saveTimer = useRef(null);
    const spotterSigPadRef = useRef(null);
    const supervisorSigPadRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            if (uuid) {
                const existingForm = await spotterTrainingService.loadByUuid(uuid);
                if (existingForm) {
                    setForm({ language: 'english', ...existingForm });
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }

            setLoading(false);
        };
        loadData();
    }, [uuid]);

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const queueSave = (formData) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDraft(formData || form), 1000);
    };

    const saveDraft = async (formData) => {
        try {
            const dataToSave = {
                ...(formData || form),
                updatedAt: new Date().toISOString()
            };
            await spotterTrainingService.saveDraft(dataToSave);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    const handleSelectSpotter = (selected) => {
        if (selected && selected.length > 0) {
            const person = selected[0];
            const personId = person.id || person.ID || person.employeeId || person.tempId || person.EE || '';
            const personName = person.FullName || person.fullName || '';

            const updated = {
                ...form,
                spotterEmployeeId: personId,
                spotterName: personName
            };
            setForm(updated);
            queueSave(updated);
        }
        setSpotterModalOpen(false);
    };

    const handleSelectSupervisor = (selected) => {
        if (selected && selected.length > 0) {
            const person = selected[0];
            const personId = person.id || person.ID || person.employeeId || person.tempId || person.EE || '';
            const personName = person.FullName || person.fullName || '';

            const updated = {
                ...form,
                supervisorEmployeeId: personId,
                supervisorName: personName
            };
            setForm(updated);
            queueSave(updated);
        }
        setSupervisorModalOpen(false);
    };

    const handleSpotterSignatureAccept = () => {
        if (!spotterSigPadRef.current) return;
        const dataUrl = spotterSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            spotterSignature: dataUrl
        };
        setForm(updated);
        setSpotterSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleSupervisorSignatureAccept = () => {
        if (!supervisorSigPadRef.current) return;
        const dataUrl = supervisorSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            supervisorSignature: dataUrl
        };
        setForm(updated);
        setSupervisorSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleClearSpotterSignature = () => {
        const updated = {
            ...form,
            spotterSignature: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleClearSupervisorSignature = () => {
        const updated = {
            ...form,
            supervisorSignature: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.spotterName) newErrors.spotterName = 'Spotter is required';
        if (!form.supervisorName) newErrors.supervisorName = 'Supervisor is required';
        if (!form.date) newErrors.date = 'Date is required';
        if (!form.spotterSignature) newErrors.spotterSignature = 'Spotter signature is required';
        if (!form.supervisorSignature) newErrors.supervisorSignature = 'Supervisor signature is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLanguageChange = (_event, newLanguage) => {
        if (submitted || !newLanguage) return;
        const updated = { ...form, language: newLanguage };
        setForm(updated);
        queueSave(updated);
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                confirmButtonColor: '#ff9800'
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                status: STATUS_SUBMITTED,
                updatedAt: new Date().toISOString()
            };

            const response = await spotterTrainingService.submit(payload);

            if (response.success) {
                // Delete from IndexedDB after successful submission
                await spotterTrainingService.delete(payload.uuid);

                setForm(payload);
                setSubmitted(true);
                setSaving(false);

                const result = await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Spotter Training form submitted successfully!',
                    confirmButtonColor: '#4caf50'
                });

                if (result.isConfirmed || result.isDismissed) {
                    navigate('/training/forms/spotter-training/list');
                }
            } else {
                setSaving(false);
                throw new Error(response.message || 'Failed to submit form');
            }
        } catch (e) {
            console.error('Error submitting form:', e);
            setSaving(false);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: e.response?.data?.message || 'Failed to submit form. Please try again.',
                confirmButtonColor: '#f44336'
            });
        }
    };

    if (loading) {
        return (
            <Box sx={{
                height: 'calc(100vh - 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/training/forms/spotter-training/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>{t.title}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ToggleButtonGroup
                            value={form.language}
                            exclusive
                            onChange={handleLanguageChange}
                            size="small"
                            disabled={submitted}
                        >
                            <ToggleButton value="english" sx={{ py: 0.25, px: 1.25, textTransform: 'none' }}>
                                English
                            </ToggleButton>
                            <ToggleButton value="spanish" sx={{ py: 0.25, px: 1.25, textTransform: 'none' }}>
                                Español
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Chip
                            label={submitted ? t.submitted : t.draft}
                            size="small"
                            sx={{
                                backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                                color: 'white'
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

                    {/* Information */}
                    <Accordion defaultExpanded sx={{
                        mb: 0.5,
                        '&.Mui-expanded': {
                            margin: '0 0 4px 0'
                        }
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{t.information}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t.spotterName}
                                        value={form.spotterName}
                                        onClick={() => !submitted && setSpotterModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSpotterModalOpen(true)}>
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        disabled={submitted}
                                        error={!!errors.spotterName}
                                        helperText={errors.spotterName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t.supervisorName}
                                        value={form.supervisorName}
                                        onClick={() => !submitted && setSupervisorModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSupervisorModalOpen(true)}>
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        disabled={submitted}
                                        error={!!errors.supervisorName}
                                        helperText={errors.supervisorName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t.date}
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => handleFieldChange('date', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={submitted}
                                        error={!!errors.date}
                                        helperText={errors.date}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Training Content */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                {t.definitionTitle}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                                {t.definitionBody} <strong>{t.definitionStrong}</strong>
                            </Typography>

                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                                {t.whenRequiredTitle}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                                {t.whenRequiredIntro(TENANT_COMPANY_CODE)}
                            </Typography>
                            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 2 }}>
                                {t.whenRequiredList.map((item, idx) => (
                                    <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>{item}</Typography>
                                ))}
                            </Box>

                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                                {t.responsibilitiesTitle}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                {t.responsibilitiesList.map((item, idx) => (
                                    <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>{item}</Typography>
                                ))}
                            </Box>

                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                    {t.noticeLine1}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', color: 'error.main' }}>
                                    {t.noticeLine2}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                {t.signaturesTitle}
                            </Typography>
                            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, md: 1.5 }}>
                                {/* Spotter Signature */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {t.spotterLabel}: {form.spotterName || t.notSelected}
                                    </Typography>
                                    <Box sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        height: 100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa',
                                        position: 'relative',
                                        cursor: submitted || form.spotterSignature ? 'default' : 'pointer',
                                    }}
                                        onClick={() => !submitted && !form.spotterSignature && setSpotterSignatureModalOpen(true)}
                                    >
                                        {form.spotterSignature ? (
                                            <>
                                                <img src={form.spotterSignature} alt="Spotter Signature" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                                {!submitted && (
                                                    <Button
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClearSpotterSignature();
                                                        }}
                                                        sx={{ position: 'absolute', top: 4, right: 4, minWidth: 'auto' }}
                                                    >
                                                        {t.clear}
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button size="small" disabled={submitted}>
                                                {t.clickToSign}
                                            </Button>
                                        )}
                                    </Box>
                                    {errors.spotterSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.spotterSignature}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Supervisor Signature */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {t.supervisorLabel}: {form.supervisorName || t.notSelected}
                                    </Typography>
                                    <Box sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        height: 100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa',
                                        position: 'relative',
                                        cursor: submitted || form.supervisorSignature ? 'default' : 'pointer',
                                    }}
                                        onClick={() => !submitted && !form.supervisorSignature && setSupervisorSignatureModalOpen(true)}
                                    >
                                        {form.supervisorSignature ? (
                                            <>
                                                <img src={form.supervisorSignature} alt="Supervisor Signature" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                                {!submitted && (
                                                    <Button
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClearSupervisorSignature();
                                                        }}
                                                        sx={{ position: 'absolute', top: 4, right: 4, minWidth: 'auto' }}
                                                    >
                                                        {t.clear}
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button size="small" disabled={submitted}>
                                                {t.clickToSign}
                                            </Button>
                                        )}
                                    </Box>
                                    {errors.supervisorSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.supervisorSignature}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? t.submitting : t.submit}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SelectEmployeesAndTempsModal
                open={spotterModalOpen}
                onClose={() => setSpotterModalOpen(false)}
                onSelect={handleSelectSpotter}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SelectEmployeesAndTempsModal
                open={supervisorModalOpen}
                onClose={() => setSupervisorModalOpen(false)}
                onSelect={handleSelectSupervisor}
                singleSelect={true}
                alreadySelected={[]}
            />
            <SignatureModalWithAuto
                open={spotterSignatureModalOpen}
                onClose={() => setSpotterSignatureModalOpen(false)}
                onAccept={handleSpotterSignatureAccept}
                sigPadRef={spotterSigPadRef}
                employeeName={form.spotterName}
                existingSignature={form.spotterSignature}
            />
            <SignatureModalWithAuto
                open={supervisorSignatureModalOpen}
                onClose={() => setSupervisorSignatureModalOpen(false)}
                onAccept={handleSupervisorSignatureAccept}
                sigPadRef={supervisorSigPadRef}
                employeeName={form.supervisorName}
                existingSignature={form.supervisorSignature}
            />

            <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
}
